/**
 * CompanyList page - Main companies page
 * Mirrors Angular company-list.component.ts/html
 * Styled to match ApplicationList/InviteeList pages
 */
import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { useIsMobile } from '@/hooks/useIsMobile'
import { useUserRole } from '@/hooks/useUserRole'
import { PageHeader } from '@/features/invitee/components/PageHeader'
import { CompanyTable } from '../components/CompanyTable'
import { CompanyGrid } from '../components/CompanyGrid'
import { CompanyFilters } from '../components/CompanyFilters'
import { useCompanyList } from '../hooks/useCompanyList'
import {
    COMPANY_LIST_PER_PAGE_OPTIONS,
    COMPANY_LIST_SEARCH_DEBOUNCE_MS,
    companyEditConfirmPresentation,
} from '../constants'
import { searchCompanies, updateCompanyListed } from '../services/companyService'
import type { Company } from '../types/company'
import type { CompanyListStatusConfirmState } from '../types/pages'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { primaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { cn } from '@/lib/utils/helpers'
import { GotoPageInput } from '@/features/application/components/GotoPageInput'
import { formatApiError, showError } from '@/lib/utils/notifications'
import { shouldRunCompanyAutocompleteSearch } from '../utils/companyListSearchLogic'

export function CompanyList() {
    const navigate = useNavigate()
    const { hasPolicy, accessibleMenu, isLibertyRole, orgId } = useUserRole()
    const canAddCompany = hasPolicy(['company_add'])
    const canWriteCompany = accessibleMenu?.company?.includes?.('Write')

    const {
        companies,
        loading,
        error,
        pagination,
        filters,
        applyFilters,
        resetFilters,
        changePage,
        changePerPage,
        sort,
        patchCompanyLibertyListed,
    } = useCompanyList()

    const isMobile = useIsMobile()
    const [isListView, setIsListView] = useState(true)
    const [isFilterOpen, setIsFilterOpen] = useState(false)
    const [confirmDialog, setConfirmDialog] = useState<CompanyListStatusConfirmState>({ isOpen: false, company: null, isCurrentlyActive: false, targetOrgId: 1 })

    // Mobile → grid view; Desktop → list view
    useEffect(() => {
        setIsListView(!isMobile)
    }, [isMobile])

    const [searchTerm, setSearchTerm] = useState(() => filters.q || '')
    const [suggestions, setSuggestions] = useState<string[]>([])
    const [showSuggestions, setShowSuggestions] = useState(false)
    const suggestionsRequestIdRef = useRef(0)
    const searchWrapperRef = useRef<HTMLDivElement>(null)
    const appliedQRef = useRef<string | undefined>(filters.q)
    const applyFiltersRef = useRef(applyFilters)
    const searchDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const suggestionsDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    // eslint-disable-next-line react-hooks/refs -- latest-value ref for stable debounce effects
    appliedQRef.current = filters.q
    // eslint-disable-next-line react-hooks/refs -- latest-value ref for stable debounce effects
    applyFiltersRef.current = applyFilters

    const listedForAutocomplete =
        filters.listed === '1' || filters.listed === '0' ? filters.listed : 'all'

    /**
     * Sync input from applied `q`, but keep local value when it only differs by whitespace.
     * Debounce applies `searchTerm.trim()`, and overwriting here would remove a trailing
     * space and jump the cursor on the first space keypress.
     */
    useLayoutEffect(() => {
        const next = filters.q || ''
        setSearchTerm((prev) => (prev.trim() === next.trim() ? prev : next))
    }, [filters.q])

    /** Debounce GET /api/company list filter on `q`. */
    useEffect(() => {
        if (searchDebounceTimerRef.current) {
            clearTimeout(searchDebounceTimerRef.current)
            searchDebounceTimerRef.current = null
        }
        searchDebounceTimerRef.current = setTimeout(() => {
            searchDebounceTimerRef.current = null
            const applied = String(appliedQRef.current ?? '').trim()
            const next = searchTerm.trim()
            if (next !== applied) {
                applyFiltersRef.current({ q: next || undefined, page: 1 })
            }
        }, COMPANY_LIST_SEARCH_DEBOUNCE_MS)
        return () => {
            if (searchDebounceTimerRef.current) {
                clearTimeout(searchDebounceTimerRef.current)
                searchDebounceTimerRef.current = null
            }
        }
    }, [searchTerm])

    /**
     * Angular `loadCompany` / `searchCompany`: GET /api/all_company on even lengths;
     * `pasteData` bypasses even-length check. Stale responses ignored via request id.
     */
    const fetchSuggestions = useCallback(
        async (text: string, opts?: { force?: boolean }) => {
            const trimmed = text.trim()
            if (!trimmed) {
                suggestionsRequestIdRef.current += 1
                setSuggestions([])
                setShowSuggestions(false)
                return
            }
            if (!opts?.force && !shouldRunCompanyAutocompleteSearch(trimmed)) {
                return
            }

            const requestId = ++suggestionsRequestIdRef.current
            try {
                const results = await searchCompanies(trimmed, listedForAutocomplete)
                if (requestId !== suggestionsRequestIdRef.current) return
                setSuggestions(results)
                setShowSuggestions(results.length > 0)
            } catch {
                if (requestId !== suggestionsRequestIdRef.current) return
                setSuggestions([])
                setShowSuggestions(false)
            }
        },
        [listedForAutocomplete],
    )

    const fetchSuggestionsRef = useRef(fetchSuggestions)
    // eslint-disable-next-line react-hooks/refs -- latest-value ref for stable debounce effects
    fetchSuggestionsRef.current = fetchSuggestions

    /** Debounce GET /api/all_company autocomplete (even-length gate inside fetchSuggestions). */
    useEffect(() => {
        if (suggestionsDebounceTimerRef.current) {
            clearTimeout(suggestionsDebounceTimerRef.current)
            suggestionsDebounceTimerRef.current = null
        }
        const trimmed = searchTerm.trim()
        if (!trimmed) {
            setSuggestions([])
            setShowSuggestions(false)
            return
        }
        suggestionsDebounceTimerRef.current = setTimeout(() => {
            suggestionsDebounceTimerRef.current = null
            void fetchSuggestionsRef.current(trimmed)
        }, COMPANY_LIST_SEARCH_DEBOUNCE_MS)
        return () => {
            if (suggestionsDebounceTimerRef.current) {
                clearTimeout(suggestionsDebounceTimerRef.current)
                suggestionsDebounceTimerRef.current = null
            }
        }
    }, [searchTerm])

    const flushSearchToFilters = useCallback(() => {
        if (searchDebounceTimerRef.current) {
            clearTimeout(searchDebounceTimerRef.current)
            searchDebounceTimerRef.current = null
        }
        const applied = String(appliedQRef.current ?? '').trim()
        const next = searchTerm.trim()
        if (next !== applied) {
            applyFiltersRef.current({ q: next || undefined, page: 1 })
        }
    }, [searchTerm])

    const selectSuggestion = useCallback(
        (name: string) => {
            if (searchDebounceTimerRef.current) {
                clearTimeout(searchDebounceTimerRef.current)
                searchDebounceTimerRef.current = null
            }
            if (suggestionsDebounceTimerRef.current) {
                clearTimeout(suggestionsDebounceTimerRef.current)
                suggestionsDebounceTimerRef.current = null
            }
            setSearchTerm(name)
            setSuggestions([])
            setShowSuggestions(false)
            applyFilters({ q: name, page: 1 })
        },
        [applyFilters],
    )

    const handleSearchPaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
        const pasted = e.clipboardData?.getData('text') ?? ''
        const el = e.currentTarget
        const start = el.selectionStart ?? el.value.length
        const end = el.selectionEnd ?? el.value.length
        const next = el.value.slice(0, start) + pasted + el.value.slice(end)
        if (suggestionsDebounceTimerRef.current) {
            clearTimeout(suggestionsDebounceTimerRef.current)
            suggestionsDebounceTimerRef.current = null
        }
        e.preventDefault()
        setSearchTerm(next)
        void fetchSuggestionsRef.current(next, { force: true })
    }, [])

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                searchWrapperRef.current &&
                !searchWrapperRef.current.contains(e.target as Node)
            ) {
                setShowSuggestions(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const handleResetAllFilters = () => {
        resetFilters()
        setSearchTerm('')
        setSuggestions([])
        setShowSuggestions(false)
        suggestionsRequestIdRef.current += 1
    }

    // Add company (navigate to React add page)
    const handleAddCompany = () => {
        navigate('/company/add')
    }

    const resolveListedOrgId = (company?: Company): number => {
        const filterOrgId = Number(filters.org_id)
        if (Number.isFinite(filterOrgId) && filterOrgId > 0) return filterOrgId

        const roleOrgId = Number(orgId)
        if (Number.isFinite(roleOrgId) && roleOrgId > 0) return roleOrgId

        const rowOrgId = Number(company?.org_id)
        if (Number.isFinite(rowOrgId) && rowOrgId > 0) return rowOrgId

        return 1
    }

    const isCompanyActive = (company: Company): boolean => {
        // Keep Liberty Listed badge consistent with selected status filter.
        if (filters.listed === '1') return true
        if (filters.listed === '0') return false

        const listedOrgId = resolveListedOrgId(company)
        return company.org_listed_ids?.[String(listedOrgId)] === 1
    }

    // Toggle listed status (matching Angular updateCompanyListed)
    const handleToggleListed = (company: Company) => {
        if (!canWriteCompany || loading) return

        const isCurrentlyActive = isCompanyActive(company)
        const targetOrgId = resolveListedOrgId(company)
        setConfirmDialog({ isOpen: true, company, isCurrentlyActive, targetOrgId })
    }

    const handleConfirmToggle = async () => {
        const { company, isCurrentlyActive, targetOrgId } = confirmDialog
        if (!company) return

        setConfirmDialog(prev => ({ ...prev, isOpen: false }))
        const previousListed = (isCurrentlyActive ? 1 : 0) as 0 | 1
        const nextListed = (isCurrentlyActive ? 0 : 1) as 0 | 1

        // Optimistic UI: flip status immediately so users see instant feedback.
        patchCompanyLibertyListed(company.company_id, nextListed, targetOrgId)
        try {
            await updateCompanyListed(company.company_id, nextListed, targetOrgId)
            // Angular `updateCompanyListed` success → `resetFilter()` (clear filters + reload defaults)
            handleResetAllFilters()
        } catch (err) {
            // Revert optimistic change when API update fails.
            patchCompanyLibertyListed(company.company_id, previousListed, targetOrgId)
            showError(formatApiError(err), false)
        }
    }

    const totalPages = pagination.last || 1
    const currentPage = pagination.current || 1

    const PaginationControls = ({
        className,
        section,
    }: {
        className?: string
        section: 'top' | 'bottom'
    }) => {
        const p = (suffix: string) => `company-list-pagination-${section}-${suffix}`
        return (
            <div className={`w-full min-w-0 sm:w-auto ${className}`}>
                <div className="flex w-full min-w-0 flex-wrap items-center justify-center gap-1 sm:gap-1.5 sm:justify-end">
                    <button
                        data-testid={p('first')}
                        className="min-w-[32px] h-8 px-2 flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        onClick={() => changePage(1)}
                        disabled={loading || currentPage === 1}
                        title="First"
                        type="button"
                    >
                        &laquo;
                    </button>
                    <button
                        data-testid={p('previous')}
                        className="min-w-[32px] h-8 px-2 flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        onClick={() => changePage(currentPage - 1)}
                        disabled={loading || currentPage === 1}
                        title="Previous"
                        type="button"
                    >
                        &lsaquo;
                    </button>

                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum: number
                        if (totalPages <= 5) {
                            pageNum = i + 1
                        } else if (currentPage <= 3) {
                            pageNum = i + 1
                        } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i
                        } else {
                            pageNum = currentPage - 2 + i
                        }
                        const isActive = currentPage === pageNum
                        return (
                            <button
                                key={pageNum}
                                data-testid={p(`page-${pageNum}`)}
                                className={`min-w-[32px] h-8 px-2 flex items-center justify-center rounded-lg text-sm font-medium transition-all ${isActive
                                    ? 'border border-button bg-button text-white shadow-md shadow-slate-300/30'
                                    : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 hover:border-slate-300'
                                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                                onClick={() => changePage(pageNum)}
                                disabled={loading}
                                type="button"
                            >
                                {pageNum}
                            </button>
                        )
                    })}

                    <button
                        data-testid={p('next')}
                        className="min-w-[32px] h-8 px-2 flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        onClick={() => changePage(currentPage + 1)}
                        disabled={loading || currentPage >= totalPages}
                        title="Next"
                        type="button"
                    >
                        &rsaquo;
                    </button>
                    <button
                        data-testid={p('last')}
                        className="min-w-[32px] h-8 px-2 flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        onClick={() => changePage(totalPages)}
                        disabled={loading || currentPage >= totalPages}
                        title="Last"
                        type="button"
                    >
                        &raquo;
                    </button>
                </div>
            </div>
        )
    }

    // Calculate active filters count (exclude search `q`, pagination/sort, default Active, default org scope)
    const isDefaultOrgScope = (val: unknown) => {
        if (isLibertyRole) return val === 1 || val === '1'
        if (orgId != null) return String(val) === String(orgId)
        return false
    }
    const activeFiltersCount = Object.entries(filters).filter(([key, value]) => {
        if (['q', 'page', 'limit', 'perPage', 'sortBy', 'orderBy', 'org_name'].includes(key)) return false;
        /** Default list view is Active; do not count that as an “extra” filter on the badge */
        if (key === 'listed' && value === '1') return false;
        /** Default API org (Liberty org 1 or company user’s org) is list scope, not a drawer filter */
        if (key === 'org_id' && isDefaultOrgScope(value)) return false;
        if (Array.isArray(value)) return value.length > 0;
        return value !== undefined && value !== null && value !== '' && value !== false;
    }).length;

    return (
        <div
            className="min-h-screen bg-[#f8fafc] font-sans text-slate-900 pb-12"
            data-testid="company-list-page"
        >
            <PageHeader title="COMPANIES" testIdRoot="company-list-page-header" />

            <div
                data-testid="company-list-main"
                className="mx-auto flex w-full min-w-0 flex-col gap-4 px-3 py-4 sm:gap-6 sm:px-6 sm:py-8"
            >
                <div
                    data-testid="company-list-toolbar"
                    className="flex w-full min-w-0 flex-col gap-4 md:flex-row md:items-center md:justify-between"
                >
                    <div
                        ref={searchWrapperRef}
                        data-testid="company-list-search-wrap"
                        className="relative w-full min-w-0 max-w-lg md:w-1/2"
                    >
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 z-10">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <circle cx="11" cy="11" r="8" />
                                <path d="m21 21-4.3-4.3" />
                            </svg>
                        </div>
                        <input
                            data-testid="company-list-search-input"
                            type="text"
                            placeholder="What are you looking for?"
                            className="w-full pl-12 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/15 focus:border-[rgb(21_151_255)] transition-all placeholder:text-slate-400"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onPaste={handleSearchPaste}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault()
                                    setShowSuggestions(false)
                                    flushSearchToFilters()
                                }
                            }}
                            onFocus={() => {
                                if (suggestions.length > 0) setShowSuggestions(true)
                            }}
                            autoComplete="off"
                        />
                        {showSuggestions && suggestions.length > 0 && (
                            <ul
                                data-testid="company-list-suggestions"
                                className="absolute left-0 right-0 top-full mt-1 z-50 max-h-60 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg"
                            >
                                {suggestions.map((name, i) => (
                                    <li
                                        key={`${name}-${i}`}
                                        data-testid={`company-list-suggestion-${i}`}
                                        className="px-4 py-2.5 text-sm text-slate-800 cursor-pointer hover:bg-slate-100 truncate"
                                        onMouseDown={() => selectSuggestion(name)}
                                    >
                                        {name}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <div
                        data-testid="company-list-toolbar-actions"
                        className="flex w-full items-center gap-2 sm:gap-3 md:w-auto"
                    >
                        <button
                            data-testid="company-list-filters-open-button"
                            onClick={() => setIsFilterOpen(true)}
                            className="relative flex-1 md:flex-none flex items-center justify-center gap-2 bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 px-5 py-2.5 rounded-xl font-semibold shadow-sm hover:shadow-md transition-all text-sm whitespace-nowrap"
                            type="button"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                            </svg>
                            Filters
                            {activeFiltersCount > 0 && (
                                <span
                                    data-testid="company-list-filters-active-count-badge"
                                    className="absolute -top-2 -right-2 bg-[rgb(21_151_255)] text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm"
                                >
                                    {activeFiltersCount}
                                </span>
                            )}
                        </button>

                        {canAddCompany && (
                            <button
                                data-testid="company-list-add-company-button"
                                onClick={handleAddCompany}
                                className={cn(primaryEntityActionButtonClass, 'flex-[2] md:flex-none gap-2')}
                                type="button"
                            >
                                <Plus className="h-5 w-5" />
                                Add Company
                            </button>
                        )}
                    </div>
                </div>

                {/* Filter Drawer */}
                <CompanyFilters
                    filters={filters}
                    onFiltersChange={applyFilters}
                    onReset={handleResetAllFilters}
                    isOpen={isFilterOpen}
                    onClose={() => setIsFilterOpen(false)}
                />

                <div
                    className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
                    data-testid="company-list-results-card"
                >
                    {error && !loading && (
                        <div
                            className="m-4 rounded-lg bg-red-50 p-4 text-center text-red-600 sm:p-8"
                            data-testid="company-list-error"
                        >
                            {error}
                        </div>
                    )}

                    {!error && (
                        <>
                            {/*
                              Angular company-list always renders top + bottom page rows
                              (outside emptyData), so empty / out-of-range pages still show controls.
                            */}
                            <div
                                data-testid="company-list-pagination-header-bar"
                                className={`flex flex-col gap-3 border-b border-slate-100 bg-slate-50/50 px-3 py-3 sm:gap-4 sm:px-6 sm:py-4 lg:flex-row lg:items-center lg:justify-between ${loading ? 'pointer-events-none' : ''}`}
                            >
                                <div
                                    data-testid="company-list-pagination-top-summary"
                                    className="flex shrink-0 flex-wrap items-center justify-center gap-1.5 text-center text-xs font-medium text-slate-500 sm:text-sm lg:max-w-md lg:justify-start lg:text-left"
                                >
                                    {totalPages > 1 ? (
                                        <>
                                            <span>Page</span>
                                            <GotoPageInput
                                                currentPage={currentPage}
                                                totalPages={totalPages}
                                                onGoto={changePage}
                                                testId="company-list-pagination-top-goto-input"
                                            />
                                            <span>
                                                of{' '}
                                                <span className="font-bold text-slate-900">{pagination.last}</span>
                                            </span>
                                        </>
                                    ) : (
                                        <span>
                                            Page{' '}
                                            <span className="font-bold text-slate-900">{pagination.current || 1}</span>
                                            {' '}of{' '}
                                            <span className="font-bold text-slate-900">{pagination.last || 1}</span>
                                        </span>
                                    )}
                                    {pagination.total > 0 && (
                                        <>
                                            <span className="mx-1 text-slate-300">|</span>
                                            <span>
                                                Total{' '}
                                                <span className="font-bold text-slate-900">{pagination.total}</span>{' '}
                                                Records
                                            </span>
                                        </>
                                    )}
                                </div>

                                <div className="flex w-full min-w-0 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end sm:gap-x-2 sm:gap-y-2 lg:flex-1">
                                    <PaginationControls className="" section="top" />
                                </div>
                            </div>

                            <div
                                data-testid="company-list-data-scroll"
                                className="min-w-0"
                            >
                                <div data-testid="company-list-data-container" className="min-w-full">
                                    {isListView ? (
                                        <CompanyTable
                                            companies={companies}
                                            onSort={sort}
                                            sortBy={filters.sortBy}
                                            orderBy={filters.orderBy}
                                            onToggleListed={handleToggleListed}
                                            isCompanyActive={isCompanyActive}
                                            disabled={loading}
                                            loading={loading}
                                        />
                                    ) : (
                                        <CompanyGrid
                                            companies={companies}
                                            onToggleListed={handleToggleListed}
                                            isCompanyActive={isCompanyActive}
                                            disabled={loading}
                                            loading={loading}
                                        />
                                    )}
                                </div>
                            </div>

                            <div
                                data-testid="company-list-pagination-footer-bar"
                                className={`flex flex-col gap-3 border-t border-slate-100 bg-slate-50/50 px-3 py-3 sm:gap-4 sm:px-6 sm:py-4 lg:flex-row lg:items-center lg:justify-between ${loading ? 'pointer-events-none' : ''}`}
                            >
                                <div
                                    data-testid="company-list-pagination-bottom-summary"
                                    className="flex shrink-0 flex-wrap items-center justify-center gap-1.5 text-center text-xs font-medium text-slate-500 sm:text-sm lg:max-w-md lg:justify-start lg:text-left"
                                >
                                    {totalPages > 1 ? (
                                        <>
                                            <span>Page</span>
                                            <GotoPageInput
                                                currentPage={currentPage}
                                                totalPages={totalPages}
                                                onGoto={changePage}
                                                testId="company-list-pagination-bottom-goto-input"
                                            />
                                            <span>
                                                of{' '}
                                                <span className="font-bold text-slate-900">{pagination.last}</span>
                                            </span>
                                        </>
                                    ) : (
                                        <span>
                                            Page{' '}
                                            <span className="font-bold text-slate-900">{pagination.current || 1}</span>
                                            {' '}of{' '}
                                            <span className="font-bold text-slate-900">{pagination.last || 1}</span>
                                        </span>
                                    )}
                                    {pagination.total > 0 && (
                                        <>
                                            <span className="mx-1 text-slate-300">|</span>
                                            <span>
                                                Total{' '}
                                                <span className="font-bold text-slate-900">{pagination.total}</span>{' '}
                                                Records
                                            </span>
                                        </>
                                    )}
                                </div>

                                <div className="flex w-full min-w-0 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end sm:gap-x-2 sm:gap-y-2 lg:flex-1">
                                    <PaginationControls className="" section="bottom" />

                                    {currentPage > 0 && (
                                        <div className="flex items-center justify-center gap-2 sm:ml-2">
                                            <span
                                                data-testid="company-list-per-page-label-bottom"
                                                className="text-xs font-semibold uppercase tracking-wide text-slate-400"
                                            >
                                                Rows
                                            </span>
                                            <select
                                                data-testid="company-list-per-page-select-bottom"
                                                className="block rounded-lg border border-slate-200 bg-white p-2 text-sm text-slate-700 focus:border-[rgb(21_151_255)] focus:ring-2 focus:ring-[rgb(21_151_255)]/25"
                                                value={filters.perPage || 15}
                                                onChange={(e) =>
                                                    changePerPage(parseInt(e.target.value, 10))
                                                }
                                            >
                                                {COMPANY_LIST_PER_PAGE_OPTIONS.map((option) => (
                                                    <option key={option} value={option}>
                                                        {option}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <ConfirmDialog
                isOpen={confirmDialog.isOpen}
                title={confirmDialog.isCurrentlyActive ? 'Confirm Company Disable?' : 'Please Confirm'}
                message={
                    confirmDialog.isCurrentlyActive
                        ? `Removing this listing will prevent potential residents from creating an application for this Company's Properties.\nAlso, disabling a Company will de-activate all Properties associated with this Company.\nPlease confirm that you want to proceed.`
                        : `Listing this property will allow potential residents to create an application for this company's properties on Liberty's site.\nAre you sure you want to proceed?`
                }
                {...companyEditConfirmPresentation}
                primaryLabel="Confirm"
                onPrimary={handleConfirmToggle}
                secondaryLabel="Cancel"
                onSecondary={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
                variant="warning"
                overlayDataTestId="company-list-listed-toggle-confirm-overlay"
                primaryButtonDataTestId="company-list-listed-toggle-confirm-primary-button"
                secondaryButtonDataTestId="company-list-listed-toggle-confirm-secondary-button"
                closeButtonDataTestId="company-list-listed-toggle-confirm-close-button"
            />
        </div>
    )
}
